import type { Doc } from "building-types/doc";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";
import objectIsEmpty from "common/utils/objectIsEmpty";
import { docGetActiveOptionTemplateOverrides } from "doc/docGetOptionTemplates";
import mergeOptionTemplatesIntoOptionSets from "doc/options/mergeOptionTemplatesIntoOptionSets";
import { overlayCatalogOntoOptionSets } from "lib/studio/resolveCatalogDisplayOptionSet";

/**
 * Merging the active option-template overrides into the doc's option sets allocates a
 * brand-new `optionSets` table on every call (see mergeOptionTemplatesIntoOptionSets).
 * Because `generateBuilding` memoizes its stages by reference, handing it a fresh
 * `optionSets` on every regeneration cascades through the entire pipeline, even for
 * unrelated edits.
 */
let mergedOptionSetsCache = new WeakMap<
  Doc["optionSets"],
  { optionTemplates: Doc["optionTemplates"]; optionSetOverrides: Doc["optionSetOverrides"]; result: Doc["optionSets"] }
>();

export function mergedOptionSetsForGeneration(doc: Doc): Doc["optionSets"] {
  const cached = mergedOptionSetsCache.get(doc.optionSets);
  if (
    cached &&
    cached.optionTemplates === doc.optionTemplates &&
    cached.optionSetOverrides === doc.optionSetOverrides
  ) {
    return cached.result;
  }

  const result = mergeOptionTemplatesIntoOptionSets(doc.optionSets, [docGetActiveOptionTemplateOverrides(doc)]);
  mergedOptionSetsCache.set(doc.optionSets, {
    optionTemplates: doc.optionTemplates,
    optionSetOverrides: doc.optionSetOverrides,
    result,
  });
  return result;
}

/**
 * Catalog-overlaid merged option sets, keyed by the merged result and the catalog table so the
 * overlaid table keeps a stable reference across regenerations (generateBuilding memoizes stages by
 * option-set identity). Returns the plain merged result when there's no catalog overlay to apply.
 */
let catalogMergedOptionSetsCache = new WeakMap<Doc["optionSets"], WeakMap<Table<DocOptionSet>, Doc["optionSets"]>>();

export function mergedOptionSetsForGenerationWithCatalog(
  doc: Doc,
  catalogOptionSets: Table<DocOptionSet> | undefined,
): Doc["optionSets"] {
  const merged = mergedOptionSetsForGeneration(doc);
  if (!catalogOptionSets || objectIsEmpty(catalogOptionSets)) {
    return merged;
  }

  let byCatalog = catalogMergedOptionSetsCache.get(merged);
  if (!byCatalog) {
    byCatalog = new WeakMap();
    catalogMergedOptionSetsCache.set(merged, byCatalog);
  }

  const cached = byCatalog.get(catalogOptionSets);
  if (cached) {
    return cached;
  }

  const result = overlayCatalogOntoOptionSets(merged, catalogOptionSets);
  byCatalog.set(catalogOptionSets, result);
  return result;
}

let docForGenerationCache = new WeakMap<Doc, WeakMap<Doc["optionSets"], Doc>>();

/**
 * The doc to hand `generateBuilding`: option templates merged in, the catalog overlay applied when
 * one is given, and one reference per (doc, resolved optionSets). The inner key is the table
 * `mergedOptionSetsForGenerationWithCatalog` returns, which is already one reference per
 * (doc, catalog), so the wrapper is too.
 *
 * Generation stages memoize on their selected params by reference. Most select doc fields, which a
 * `{ ...doc, optionSets }` wrapper leaves untouched, but `generateOptionSetEntityRelations` and
 * `generateAnnotation` select the doc itself, and both `optionSetEntityRelationsFromDoc` and the
 * `DocEntityNodeRegistry` node cache key on it. A wrapper assembled inline at each call site would
 * miss those every time. Use this instead.
 */
export function docForGeneration(doc: Doc, catalogOptionSets?: Table<DocOptionSet>): Doc {
  const optionSets = mergedOptionSetsForGenerationWithCatalog(doc, catalogOptionSets);

  let byOptionSets = docForGenerationCache.get(doc);
  if (!byOptionSets) {
    byOptionSets = new WeakMap();
    docForGenerationCache.set(doc, byOptionSets);
  }

  const cached = byOptionSets.get(optionSets);
  if (cached) {
    return cached;
  }

  const result: Doc = { ...doc, optionSets };
  byOptionSets.set(optionSets, result);
  return result;
}

/** Resets the memo. Exposed for tests so cache state does not leak between cases. */
export function clearMergedOptionSetsCache(): void {
  mergedOptionSetsCache = new WeakMap();
  catalogMergedOptionSetsCache = new WeakMap();
  docForGenerationCache = new WeakMap();
}
